import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Plus, Trash2, Edit2, X, Users } from "lucide-react";

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/suppliers");
      setSuppliers(r.data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try { await api.delete(`/admin/suppliers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = suppliers.filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.gstin?.toLowerCase().includes(q) || s.phone?.includes(q);
  });

  return (
    <div className="space-y-6" data-testid="admin-suppliers">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Party Ledger · Sundry Creditors</div>
          <div className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Suppliers Master</div>
          <div className="text-xs text-white/50 mt-1">
            Tally-style vendor ledger — GSTIN, PAN, address, opening balance. Reused wherever a purchase is booked.
          </div>
        </div>
        <div className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name / GSTIN / phone"
            className="bg-black/40 border border-white/15 px-3 py-2 text-sm w-64"
            data-testid="supplier-search" />
          <button onClick={() => setEditing({})} data-testid="new-supplier-btn"
            className="inline-flex items-center gap-2 bg-brand-orange text-black px-4 py-2 font-bold text-xs uppercase tracking-widest">
            <Plus size={14} /> New Supplier
          </button>
        </div>
      </div>

      {loading && <div className="text-white/40 py-8 text-center">Loading…</div>}
      {!loading && (
        <div className="border border-white/12 bg-[#131A2B]">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">GSTIN</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-left px-3 py-2">City / State</th>
                <th className="text-right px-3 py-2">Opening ₹</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="text-center py-12 text-white/40">
                  <Users size={28} className="mx-auto mb-2 opacity-30" />
                  No suppliers yet — click <span className="brand-orange">New Supplier</span> to add one.
                </td></tr>
              )}
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-white/6 hover:bg-white/5" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-3 py-2 font-bold">{s.name}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/70">{s.gstin || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.phone || "—"}</td>
                  <td className="px-3 py-2 text-xs">{s.city || "—"}{s.state ? ` · ${s.state}` : ""}</td>
                  <td className="px-3 py-2 text-right font-mono brand-orange">₹ {(s.opening_balance || 0).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(s)} className="text-brand-blue hover:brand-orange mr-2" data-testid={`edit-supplier-${s.id}`}><Edit2 size={12} /></button>
                    <button onClick={() => del(s.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-supplier-${s.id}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SupplierEditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SupplierEditModal({ initial, onClose, onDone }) {
  const [form, setForm] = useState({
    id: initial.id || undefined,
    name: initial.name || "",
    gstin: initial.gstin || "",
    phone: initial.phone || "",
    email: initial.email || "",
    address_line1: initial.address_line1 || "",
    city: initial.city || "",
    state: initial.state || "",
    state_code: initial.state_code || "",
    pincode: initial.pincode || "",
    pan: initial.pan || "",
    opening_balance: initial.opening_balance || 0,
    notes: initial.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/suppliers", { ...form, opening_balance: Number(form.opening_balance || 0) });
      toast.success(form.id ? "Supplier updated" : "Supplier created");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="supplier-edit-modal"
        className="w-full max-w-lg bg-[#131A2B] border border-brand-orange/50 my-4">
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Supplier ledger</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">{form.id ? "Edit" : "New"} Supplier</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50"><X size={16} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <F label="Name" span={2}><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="s-name" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
          <F label="GSTIN"><input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="s-gstin" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></F>
          <F label="PAN"><input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="s-pan" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></F>
          <F label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
          <F label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
          <F label="Address" span={2}><input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
          <F label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
          <F label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
          <F label="State Code"><input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></F>
          <F label="Pincode"><input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></F>
          <F label="Opening balance ₹" span={2}>
            <input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              data-testid="s-opening" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </F>
          <F label="Notes" span={2}><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></F>
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="s-save"
            className="flex-[2] bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? "Saving…" : (form.id ? "Update Supplier" : "Create Supplier")}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</label>
      {children}
    </div>
  );
}
